import { useEffect, useCallback, useRef } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useRoom } from '@/hooks/useRoom.js'
import { useVideo } from '@/hooks/useVideo.js'
import { useChess } from '@/hooks/useChess.js'
import VideoPanel from '@/components/video/VideoPanel.jsx'
import ChessPanel from '@/components/chess/ChessPanel.jsx'
import RoomHeader from '@/components/room/RoomHeader.jsx'
import InvitePanel from '@/components/room/InvitePanel.jsx'
import GameOverModal from '@/components/chess/GameOverModal.jsx'
import DisconnectedBanner from '@/components/room/DisconnectedBanner.jsx'
import LoadingScreen from '@/components/ui/LoadingScreen.jsx'
import ErrorScreen from '@/components/ui/ErrorScreen.jsx'
import ClosedRoomScreen from '@/components/room/ClosedRoomScreen.jsx'
import { useChessSounds } from '@/hooks/useChessSounds.js'

export default function RoomPage() {
  const { roomId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { preload, playMove, playCapture, playCheck, playCheckmate, playJoin } = useChessSounds()

  const role = searchParams.get('role') === 'creator' ? 'creator' : 'guest'
  const isCreator = role === 'creator'
  const playerColor = isCreator ? 'white' : 'black'

  const {
    room,
    loading: roomLoading,
    error: roomError,
    guestJoined,
    creatorPeerId,
    startGame,
    finishGame,
    restartGame,
    closeRoom,
  } = useRoom(roomId, role)

  const {
    localStream,
    remoteStream,
    callConnected,
    mediaError,
    isMuted,
    isCameraOff,
    connectionStatus,
    toggleMute,
    toggleCamera,
  } = useVideo(roomId, isCreator)

  // Preload sounds on mount so first move plays instantly
  useEffect(() => { preload() }, [preload])

  const gameActive = room?.status === 'active'
  const gameWasActiveRef = useRef(false)
  useEffect(() => {
    if (gameActive) gameWasActiveRef.current = true
  }, [gameActive])

  const prevGuestJoinedRef = useRef(false)
  useEffect(() => {
    if (guestJoined && !prevGuestJoinedRef.current) playJoin()
    prevGuestJoinedRef.current = guestJoined
  }, [guestJoined, playJoin])

  const {
    fen,
    moveHistory,
    gameOver,
    lastMove,
    promotionPending,
    syncing,
    currentTurn,
    isCheck,
    isMyTurn,
    makeMove,
    handlePromotion,
    resetGame,
    cancelPromotion,
    getLegalMovesFrom,
    getCurrentGame,
  } = useChess(roomId, playerColor, gameActive)


  // Persist game result once, from creator only
  const finishedRef = useRef(null)
  useEffect(() => {
    if (!gameOver || !isCreator) return
    const key = `${gameOver.reason}-${gameOver.winner}`
    if (finishedRef.current === key) return
    finishedRef.current = key
    finishGame({ reason: gameOver.reason, winner: gameOver.winner })
  }, [gameOver, isCreator, finishGame])

  // Play sounds on move history changes
  const prevMoveCountRef = useRef(0)
  useEffect(() => {
    if (!gameActive) return
    const count = moveHistory.length
    if (count <= prevMoveCountRef.current) return
    prevMoveCountRef.current = count

    if (gameOver?.reason === 'checkmate') { playCheckmate(); return }

    const lastMove = moveHistory[count - 1]
    const san = lastMove?.san || ''
    const isCapture = san.includes('x')
    const isCheck = san.includes('+')

    if (isCheck) playCheck()
    else if (isCapture) playCapture()
    else playMove()
  }, [moveHistory, gameActive, gameOver, playMove, playCapture, playCheck, playCheckmate])

  const handleStartGame = useCallback(() => startGame(), [startGame])

  const handleRestartGame = useCallback(async () => {
    finishedRef.current = null
    gameWasActiveRef.current = false
    await resetGame()
    await restartGame()
  }, [resetGame, restartGame])

  // Guest: watch for creator resetting the game (game_result clears to null)
  const prevGameResultRef = useRef(undefined)
  useEffect(() => {
    if (isCreator) return
    const prev = prevGameResultRef.current
    const curr = room?.game_result ?? null
    prevGameResultRef.current = curr
    // If it just flipped from something → null, creator restarted — reset our chess state
    if (prev !== undefined && prev !== null && curr === null) {
      resetGame()
    }
  }, [room?.game_result]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseRoom = useCallback(async () => {
    await closeRoom(roomId)
    navigate('/')
  }, [closeRoom, roomId, navigate])

  const handleLeaveRoom = useCallback(() => navigate('/'), [navigate])

  // Delete room if creator closes or refreshes the tab
  useEffect(() => {
    if (!isCreator || !roomId) return
    const onUnload = () => {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rooms?id=eq.${roomId}`
      try {
        fetch(url, {
          method: 'DELETE',
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          keepalive: true,
        })
      } catch (_) {}
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [isCreator, roomId])

  // ─── guards ───────────────────────────────────────────────────────────────

  if (roomLoading) return <LoadingScreen message="Joining room…" />
  if (roomError) return <ErrorScreen message={roomError} onBack={() => navigate('/')} />
  if (!room) return <ErrorScreen message="Room not found." onBack={() => navigate('/')} />
  if (room.status === 'closed') return <ClosedRoomScreen onBack={() => navigate('/')} />

  const showDisconnected = !callConnected && guestJoined && connectionStatus === 'disconnected'
  const showWaitingForGuest = isCreator && !guestJoined
  const showGameOver = !!gameOver && gameWasActiveRef.current

  return (
    <div className="min-h-dvh flex flex-col bg-obsidian-950">
      <RoomHeader
        room={room}
        role={role}
        guestJoined={guestJoined}
        currentTurn={currentTurn}
        isCheck={isCheck}
        gameActive={gameActive}
      />

      <main className="flex-1 flex flex-col lg:flex-row gap-4 p-4 max-w-[1400px] mx-auto w-full">
        {/* Left: Video + Invite + Start */}
        <aside className="lg:w-[340px] xl:w-[380px] flex-shrink-0 flex flex-col gap-4">
          <VideoPanel
            localStream={localStream}
            remoteStream={remoteStream}
            callConnected={callConnected}
            mediaError={mediaError}
            isMuted={isMuted}
            isCameraOff={isCameraOff}
            connectionStatus={connectionStatus}
            toggleMute={toggleMute}
            toggleCamera={toggleCamera}
            isCreator={isCreator}
            guestJoined={guestJoined}
          />

          {showWaitingForGuest && <InvitePanel roomId={roomId} />}

          {isCreator && guestJoined && !gameActive && !showGameOver && (
            <button
              onClick={handleStartGame}
              className="btn-primary w-full py-3.5 text-base rounded-xl"
              style={{ boxShadow: '0 8px 24px rgba(212,168,32,0.2)' }}
            >
              <span className="text-lg">♟</span>
              Start Chess Match
            </button>
          )}
        </aside>

        {/* Right: Chess */}
        <section className="flex-1 min-w-0 flex flex-col gap-4">
          {showDisconnected && (
            <DisconnectedBanner
              isCreator={isCreator}
              onClose={isCreator ? handleCloseRoom : handleLeaveRoom}
            />
          )}
          <ChessPanel
            fen={fen}
            moveHistory={moveHistory}
            lastMove={lastMove}
            promotionPending={promotionPending}
            syncing={syncing}
            currentTurn={currentTurn}
            isMyTurn={isMyTurn}
            isCheck={isCheck}
            playerColor={playerColor}
            gameActive={gameActive}
            makeMove={makeMove}
            handlePromotion={handlePromotion}
            cancelPromotion={cancelPromotion}
            isCreator={isCreator}
            guestJoined={guestJoined}
            getLegalMovesFrom={getLegalMovesFrom}
            getCurrentGame={getCurrentGame}
          />
        </section>
      </main>

      {showGameOver && (
        <GameOverModal
          gameOver={gameOver}
          playerColor={playerColor}
          isCreator={isCreator}
          onRestart={isCreator ? handleRestartGame : null}
          onLeave={handleLeaveRoom}
          onClose={isCreator ? handleCloseRoom : handleLeaveRoom}
        />
      )}
    </div>
  )
}